import { supabase } from '@/lib/supabase'
import type { GalleryPhoto, GalleryAllImages, PaginatedResult } from '../types'
import { GalleryDataAccess } from './galleryDataAccess'
import { normalizeError } from './errors'

const FALLBACK_REVIEW_STATUS: GalleryAllImages['review_status'] = 'approved'

function transformLegacyPhotos(photos: Array<Partial<GalleryPhoto>>): GalleryAllImages[] {
  return photos.map((photo) => ({
    image_type: 'gallery_photo',
    original_id: photo.id ?? 0,
    gallery_id: photo.gallery_id ?? 0,
    image_url: photo.image_url ?? '',
    caption: photo.caption ?? null,
    review_status: FALLBACK_REVIEW_STATUS,
    created_at: photo.created_at ?? new Date().toISOString(),
    sort_order: photo.sort_order ?? 0,
    gallery_title: null,
    franchise_region: null,
    job_id: null,
    job_number: null,
    is_cover: null,
    jobber_web_uri: null
  })) as GalleryAllImages[]
}

type GalleryJobImageRow = {
  id: number
  gallery_id: number
  is_included?: boolean | null
  is_cover?: boolean | null
  sort_order?: number | null
  created_at?: string | null
  franchise_image_jobs?: {
    id?: number
    image_url?: string | null
    file_name?: string | null
    job_id?: string | null
    job_number?: number | null
    franchise_region?: string | null
    review_status?: GalleryAllImages['review_status'] | null
    created_on?: string | null
    jobber_web_uri?: string | null
  } | null
}

const normalizeSortOrder = (value?: number | null) => (
  typeof value === 'number' ? value : Number.MAX_SAFE_INTEGER
)

function transformJobImages(rows: GalleryJobImageRow[]): GalleryAllImages[] {
  return (rows || []).map((row) => {
    const job = row.franchise_image_jobs || {}
    return {
      image_type: 'job_image' as const,
      original_id: row.id,
      gallery_id: row.gallery_id,
      gallery_title: null, // Not available from job images query
      image_url: job.image_url || '',
      caption: job.file_name ?? null,
      review_status: (job.review_status || 'pending') as GalleryAllImages['review_status'],
      created_at: job.created_on || row.created_at || new Date().toISOString(),
      job_id: job.job_id ?? null,
      job_number: job.job_number ?? null,
      franchise_region: (job.franchise_region ?? null) as GalleryAllImages['franchise_region'],
      is_cover: row.is_cover ?? null,
      is_included: row.is_included ?? null,
      sort_order: row.sort_order ?? null,
      file_name: job.file_name ?? undefined,
      jobber_web_uri: job.jobber_web_uri ?? undefined,
      // Expose franchise_image_jobs.id for status updates and crop operations
      franchise_image_job_id: job.id ?? null
    }
  })
}

async function fetchFromJobLinks(galleryId: number) {
  const { data, error } = await supabase
    .from('gallery_job_images')
    .select(`
      id,
      gallery_id,
      is_included,
      is_cover,
      sort_order,
      created_at,
      franchise_image_jobs (
        id,
        image_url,
        file_name,
        job_id,
        job_number,
        franchise_region,
        review_status,
        created_on,
        jobber_web_uri
      )
    `)
    .eq('gallery_id', galleryId)
    .order('sort_order', { ascending: true })

  if (error) throw error
  return transformJobImages((data || []) as GalleryJobImageRow[])
}

async function fetchFromLegacyPhotos(galleryId: number, opts?: { from?: number; to?: number; withCount?: boolean }) {
  let query = supabase
    .from('gallery_photos')
    .select('*', { count: opts?.withCount ? 'estimated' : undefined })
    .eq('gallery_id', galleryId)
    .order('sort_order', { ascending: true })

  if (typeof opts?.from === 'number' && typeof opts?.to === 'number') {
    query = query.range(opts.from, opts.to)
  }

  const { data, error, count } = await query
  if (error) throw error
  const normalized = transformLegacyPhotos(data || [])
  return { data: normalized, count: count ?? normalized.length }
}

export async function fetchGalleryAllImages(galleryId: number): Promise<GalleryAllImages[]> {
  if (!galleryId) return []
  try {
    const [jobImages, photoData] = await Promise.all([
      fetchFromJobLinks(galleryId),
      fetchFromLegacyPhotos(galleryId)
    ])

    const combined = [...photoData.data, ...jobImages]

    return combined.sort((a, b) => {
      const sortDelta = normalizeSortOrder(a.sort_order) - normalizeSortOrder(b.sort_order)
      if (sortDelta !== 0) return sortDelta
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0
      return aTime - bTime
    })
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch gallery images')
  }
}

export async function fetchGalleryImagesPage(params: {
  galleryId: number
  page?: number
  pageSize?: number
}): Promise<PaginatedResult<GalleryAllImages>> {
  const page = params.page && params.page > 0 ? params.page : 1
  const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 24
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1

  try {
    const allImages = await fetchGalleryAllImages(params.galleryId)
    return {
      items: allImages.slice(from, to + 1),
      totalCount: allImages.length,
      page,
      pageSize
    }
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch gallery images')
  }
}

export async function fetchAllImages(
  page = 1,
  pageSize = 24,
  sortBy: 'created_at' | 'job_number' = 'created_at',
  sortDir: 'asc' | 'desc' = 'desc'
) {
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  try {
    const { data, error, count } = await supabase
      .from('gallery_all_images')
      .select('*', { count: 'estimated' })
      .order(sortBy, { ascending: sortDir === 'asc' })
      .range(from, to)

    if (error) throw error
    return { images: (data || []) as GalleryAllImages[], totalCount: count || 0, page, pageSize }
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch all images')
  }
}

export async function uploadImage(galleryId: number, file: File, caption?: string): Promise<GalleryPhoto> {
  try {
    const unified = await GalleryDataAccess.uploadGalleryImage({ galleryId, file, caption })
  // Convert to legacy for now
    return {
      id: unified.original_id,
      gallery_id: unified.gallery_id,
      image_url: unified.image_url,
      caption: unified.caption ?? null,
      sort_order: unified.sort_order || 0,
      created_at: unified.created_at
    }
  } catch (err) {
    throw normalizeError(err, 'Failed to upload image')
  }
}

export async function deleteImage(imageId: number, imageType: 'job_image' | 'gallery_photo' = 'job_image'): Promise<void> {
  try {
    await GalleryDataAccess.deleteGalleryImage(imageId, imageType)
  } catch (err) {
    throw normalizeError(err, 'Failed to delete image')
  }
}

export async function updateCaption(imageId: number, caption: string, imageType: 'job_image' | 'gallery_photo' = 'job_image') {
  try {
    return await GalleryDataAccess.updateImageCaption(imageId, caption, imageType)
  } catch (err) {
    throw normalizeError(err, 'Failed to update caption')
  }
}

// Reorder images by updating sort_order on appropriate tables
export async function reorderMixedImages(items: Array<{ id: number, type: 'job_image' | 'gallery_photo' }>): Promise<void> {
  try {
    // Process in parallel groups
    const jobImageUpdates = items
      .map((item, index) => ({ ...item, index }))
      .filter(item => item.type === 'job_image')
      .map(item => ({ id: item.id, sort_order: item.index }))

    const galleryPhotoUpdates = items
      .map((item, index) => ({ ...item, index }))
      .filter(item => item.type === 'gallery_photo')
      .map(item => ({ id: item.id, sort_order: item.index }))

    const jobPromise = jobImageUpdates.length > 0
      ? supabase.from('gallery_job_images').upsert(jobImageUpdates, { onConflict: 'id' })
      : Promise.resolve({ error: null })

    const photoPromise = galleryPhotoUpdates.length > 0
      ? supabase.from('gallery_photos').upsert(galleryPhotoUpdates, { onConflict: 'id' })
      : Promise.resolve({ error: null })

    const [jobResult, photoResult] = await Promise.all([jobPromise, photoPromise])

    if (jobResult?.error) throw jobResult.error
    if (photoResult?.error) throw photoResult.error
  } catch (err) {
    throw normalizeError(err, 'Failed to reorder images')
  }
}

export async function bulkUpdateJobImages(ids: number[], status: 'approved' | 'rejected'): Promise<void> {
  try {
    const { error } = await supabase
      .from('franchise_image_jobs')
      .update({ review_status: status })
      .in('id', ids)
    
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to bulk update job images')
  }
}

export async function reorderImages(linkIdsInOrder: number[]): Promise<void> {
  // Legacy/Job-only reorder kept for backward compatibility if needed, 
  // but reorderMixedImages is preferred for unified views.
  try {
    const updates = linkIdsInOrder.map((id, index) => ({ id, sort_order: index }))
    const { error } = await supabase
      .from('gallery_job_images')
      .upsert(updates, { onConflict: 'id' })
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to reorder images')
  }
}

export async function linkJobImages(galleryId: number, jobImageIds: number[]): Promise<void> {
  try {
    if (!jobImageIds.length) return
    const rows = jobImageIds.map((jobId, idx) => ({ gallery_id: galleryId, job_id: jobId, is_included: true, sort_order: idx }))
    const { error } = await supabase.from('gallery_job_images').insert(rows)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to link job images')
  }
}

export async function unlinkJobImages(linkIds: number[]): Promise<void> {
  try {
    if (!linkIds.length) return
    const { error } = await supabase.from('gallery_job_images').delete().in('id', linkIds)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to unlink job images')
  }
}

export async function updateJobImageStatus(jobId: number, status: 'approved' | 'rejected' | 'under_review'): Promise<void> {
  try {
    const { error } = await supabase
      .from('franchise_image_jobs')
      .update({ review_status: status })
      .eq('id', jobId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to update job image status')
  }
}

export async function toggleImageInclusion(galleryJobImageId: number, isIncluded: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('gallery_job_images')
      .update({ is_included: isIncluded })
      .eq('id', galleryJobImageId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to toggle image inclusion')
  }
}

export async function setPhotoPhase(
  originalId: number, 
  phase: 'before' | 'during' | 'after', 
  imageType: 'job_image' | 'gallery_photo'
): Promise<void> {
  try {
    const table = imageType === 'job_image' ? 'gallery_job_images' : 'gallery_photos'
    const { error } = await supabase
      .from(table)
      .update({ phase })
      .eq('id', originalId)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to set photo phase')
  }
}
